import React from 'react';
import PropTypes from 'prop-types';
import { map, pickBy, has, isEmpty } from 'lodash';
import classnames from 'classnames';
import { CircularProgress, IconButton, Radio } from '@mui/material';
import { Done as CheckIcon, MoreVert } from '@mui/icons-material';
import { ReactComponent as Expand } from 'js/components/elements/treeview/assets/expandMoreFilled.svg';
import { filter } from 'js/components/elements/treeview/TreeView';
import cx from 'classnames';
import PreviewButton from 'js/components/destination/destination-content/database/PreviewButton';
import { CONNECTION_CATEGORY_NOSQL } from 'js/constants/connectionCategories';
import { Menu } from 'new/elements/Menu/Menu';
import { useDispatch } from 'react-redux';
import { probeGetSample } from 'js/actions';
import { TableColumnsSkeleton } from 'new/components/database/TableColumnsSkeleton';
import { ReactComponent as PrimarykeyIcon } from 'new/components/icons/primary-key.svg';

export function TableColumns({ columns }) {
  const hasDefaultValue = columns?.some((c) => has(c, 'defaultValue')) ?? false;

  return (
    <table className="columns-table">
      <thead>
        <tr>
          <th className="columns-table-col-15">Column</th>
          <th className="columns-table-col-15">Type</th>
          <th className="columns-table-col-15">Primary</th>
          {hasDefaultValue && <th className="columns-table-col-15">Default</th>}
        </tr>
      </thead>
      <tbody>
        {map(columns, (c, ix) => (
          <tr key={c.name}>
            <td className="columns-table-col-15">{c.name}</td>
            <td className="columns-table-col-15 type-color">{c.type}</td>
            <td className="columns-table-col-15">
              <span>{c.primaryKey ? <PrimarykeyIcon width={16} height={16} /> : null}</span>
            </td>
            {hasDefaultValue && (
              <td
                className={classnames('columns-table-col-15', {
                  'missing-default-value': !has(c, 'defaultValue'),
                })}
              >
                {has(c, 'defaultValue') ? String(c.defaultValue) : '(none)'}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const circularProgressElement = (
  <CircularProgress
    size={16}
    thickness={4}
    color="primary"
    className="table-node-handler-loading"
  />
);

class TableItem extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isSelecting: false,
      isExpanding: false,
      isWaitingForData: false,
      menuAnchorEl: null,
    };

    this._onExpand = this._onExpand.bind(this);
    this._onSelect = this._onSelect.bind(this);
    this._onMenuOpen = this._onMenuOpen.bind(this);
    this._onMenuClose = this._onMenuClose.bind(this);
  }

  componentDidUpdate(prevProps) {
    const { t, probeDataFlat } = this.props;

    const probeJustScanned =
      prevProps.probeDataFlat !== probeDataFlat &&
      probeDataFlat[t.id]?.scanned &&
      !prevProps.probeDataFlat[t.id]?.scanned;

    // When the probe completes without an active expand gesture, flag that we're
    // waiting for the table data to propagate through DatabaseItem's local state.
    if (probeJustScanned && !this.state.isExpanding) {
      this.setState({ isWaitingForData: true });
    } else if (
      (this.state.isSelecting || this.state.isExpanding || this.state.isWaitingForData) &&
      probeDataFlat[t.id]?.scanned &&
      t !== prevProps.t
    ) {
      // t prop updated (columns arrived) — safe to drop all loading flags
      this.setState({ isSelecting: false, isExpanding: false, isWaitingForData: false });
    }
  }

  renderExpandedContent() {
    const { t, probeDataFlat } = this.props;
    const { isExpanding, isWaitingForData } = this.state;
    // Use probeDataFlat[t.id]?.scanned rather than isProbingTree so the skeleton
    // persists across gaps between chained probes (e.g. root → db → table).
    const tableScanned = probeDataFlat[t.id]?.scanned;
    const isLoading = isExpanding || isWaitingForData || !tableScanned;
    const hasColumns = !isEmpty(t.columns);

    if (isLoading) {
      return <TableColumnsSkeleton />;
    }

    if (!hasColumns) {
      return <div className="empty-database-message">This table does not have a schema.</div>;
    }

    return <TableColumns columns={t.columns} />;
  }

  render() {
    const {
      t,
      disableSelection,
      selected,
      expanded,
      connectionCategory,
      onFetchSamples,
      shouldShowExpandedView,
      menuItems,
    } = this.props;
    const { isExpanding, menuAnchorEl } = this.state;
    const isLoading = isExpanding;
    const shouldShowExpandedContent = expanded && shouldShowExpandedView;
    return (
      <li
        className={classnames('table-node', { expanded, selected })}
        data-testid={`table-node-${t.name}`}
      >
        <div className="table-node-content">
          {shouldShowExpandedView && (
            <span className="table-node-handler ">
              {isLoading && circularProgressElement}

              {!isLoading && (
                <div className="expand-icon-button" onClick={this._onExpand}>
                  <Expand
                    className={cx('expand', {
                      ['expand-opened']: expanded,
                      ['expand-closed']: !expanded,
                    })}
                  />
                </div>
              )}
            </span>
          )}

          <div
            data-testid="table-node-selector"
            className="table-node-detail"
            onClick={!disableSelection ? this._onSelect : undefined}
          >
            <div key={t.id} className={'table-item'}>
              <div className="table-node-detail-icon" />
              <span className="table-title">{t.name}</span>
            </div>
            {menuItems && menuItems.length > 0 ? (
              <>
                <span className="table-row-menu-trigger">
                  <IconButton
                    size="small"
                    onClick={this._onMenuOpen}
                    data-testid="table-row-menu"
                    aria-label="Table actions"
                    aria-controls={menuAnchorEl ? `table-row-menu-${t.id}` : undefined}
                    aria-haspopup="true"
                    aria-expanded={!!menuAnchorEl}
                  >
                    <MoreVert fontSize="small" />
                  </IconButton>
                </span>
                <Menu
                  id={`table-row-menu-${t.id}`}
                  anchorEl={menuAnchorEl}
                  open={Boolean(menuAnchorEl)}
                  onClose={this._onMenuClose}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  MenuListProps={{
                    'aria-labelledby': `table-row-menu-${t.id}`,
                  }}
                  items={menuItems.map((item) => ({
                    ...item,
                    onClick: () => {
                      this._onMenuClose();
                      item.onClick?.();
                    },
                  }))}
                />
              </>
            ) : onFetchSamples ? (
              <PreviewButton onClick={() => onFetchSamples(t.id)} />
            ) : null}
          </div>
        </div>

        {shouldShowExpandedContent && this.renderExpandedContent()}
      </li>
    );
  }

  _onExpand(e) {
    const { t, probeDataFlat, expanded, expandTable } = this.props;

    e.stopPropagation();

    if (expanded) {
      expandTable(null);
    } else if (probeDataFlat[t.id] && probeDataFlat[t.id].scanned) {
      expandTable(t.id);
    } else {
      this.setState({ isExpanding: true }, () => {
        expandTable(t.id);
      });
    }
  }

  _onSelect(e) {
    const { t, selectTable } = this.props;

    e.stopPropagation();

    this.setState({ isSelecting: true }, () => {
      selectTable(t.id);
    });
  }

  _onMenuOpen(e) {
    e.stopPropagation();
    this.setState({ menuAnchorEl: e.currentTarget });
  }

  _onMenuClose() {
    this.setState({ menuAnchorEl: null });
  }
}

export default function TableList({
  tables,
  search,
  selectTable,
  expandTable,
  expandedTableId,
  selectedTable,
  disableSelection,
  probeDataFlat,
  credentialId,
  database,
  connectionCategory,
  shouldShowExpandedView = true,
  onPreviewTable,
  isProbingTree = false,
}) {
  const filteredTables = search ? pickBy(tables, filter(search)) : tables;

  const dispatch = useDispatch();

  const onFetchNoSQLSamples = (collection, database) => () => {
    dispatch(probeGetSample(credentialId, 0, { collection, database }));
  };

  const isExpanded = (t) => t.id === expandedTableId;
  const isSelected = (t) => t.id === (selectedTable && selectedTable.id);

  const getOnFetchSamples = (t) => {
    if (onPreviewTable) {
      return () => onPreviewTable(t.id);
    }
    if (connectionCategory === CONNECTION_CATEGORY_NOSQL) {
      return onFetchNoSQLSamples(t.name, database.name);
    }
    return undefined;
  };

  return (
    <ul className="table-list">
      {map(filteredTables, (t, ix) => {
        const expanded = isExpanded(t);
        const selected = isSelected(t);
        const menuItems = onPreviewTable
          ? [
              {
                label: 'Preview',
                'data-testid': 'table-preview-option',
                onClick: () => onPreviewTable(t.id),
              },
            ]
          : null;

        return (
          <TableItem
            key={ix}
            t={t}
            onFetchSamples={getOnFetchSamples(t)}
            menuItems={menuItems}
            selectTable={selectTable}
            expandTable={expandTable}
            selected={selected}
            expanded={expanded}
            isProbingTree={isProbingTree}
            connectionCategory={connectionCategory}
            disableSelection={disableSelection}
            probeDataFlat={probeDataFlat}
            shouldShowExpandedView={shouldShowExpandedView}
          />
        );
      })}
    </ul>
  );
}

TableList.propTypes = {
  shouldShowExpandedView: PropTypes.bool,
  onPreviewTable: PropTypes.func,
};

TableList.defaultProps = {
  shouldShowExpandedView: true,
};
